'use client';

import type { FC, MutableRefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { PerspectiveCamera, Vector3 } from 'three';
import type { CameraFrame } from '../scene/SkyCamera';
import styles from './FlightHud.module.css';
import {
  advanceFlight,
  applyFisheye,
  geodeticToScene,
  type FlightState,
} from '../geo';
import { skyState } from '../skyStore';

export const FlightHud: FC<{
  cameraRef: MutableRefObject<CameraFrame | null>;
  flights: FlightState[];
  interactive: boolean;
  onHover: (id: string | null) => void;
}> = ({ cameraRef, flights, interactive, onHover }) => {
  const [hovered, setHovered] = useState<FlightState | null>(null);
  const readoutRef = useRef<HTMLDivElement>(null);
  const pointer = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      pointer.current = { x: e.clientX, y: e.clientY };
    };
    const onLeave = () => {
      pointer.current = null;
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerleave', onLeave);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerleave', onLeave);
    };
  }, []);

  useEffect(() => {
    if (!interactive) {
      if (readoutRef.current) readoutRef.current.style.opacity = '0';
      if (hovered) {
        setHovered(null);
        onHover(null);
      }
      return;
    }

    const camera = new PerspectiveCamera(55, 1, 1, 4e5);
    camera.rotation.order = 'YXZ';
    const v = new Vector3();
    let raf = 0;

    const frame = () => {
      raf = requestAnimationFrame(frame);
      const readout = readoutRef.current;
      const cursor = pointer.current;
      if (!readout) return;
      if (!cursor) {
        readout.style.opacity = '0';
        if (hovered) {
          setHovered(null);
          onHover(null);
        }
        return;
      }

      const cam = cameraRef.current;
      if (!cam) return;

      const w = window.innerWidth;
      const h = window.innerHeight;
      camera.position.set(0, cam.height, 0);
      camera.rotation.set(cam.pitch, cam.yaw, 0);
      camera.fov = cam.fov;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      camera.updateMatrixWorld(true);

      const nowSec = Date.now() / 1000;
      let best: FlightState | null = null;
      // The target is the reticle, not the dot inside it.
      let bestDist = Math.max(12, skyState.traffic.reticleSize * 0.75);
      let bestX = 0;
      let bestY = 0;
      const { fisheyeStrength, fisheyeZoom } = skyState.post;

      for (const f of flights) {
        const age = Math.min(240, Math.max(0, nowSec - f.t));
        const here = advanceFlight(f, age);
        const p = geodeticToScene(here.lat, here.lon, f.alt);
        v.set(p.x, p.y, p.z).project(camera);
        if (v.z >= 1) continue;
        // Match the lens: the scene is distorted in post, so the hit-test has
        // to be too, or the target sits away from where it's drawn.
        const d2 = applyFisheye(v.x, v.y, fisheyeStrength, fisheyeZoom, w, h);
        const x = ((d2.x + 1) / 2) * w;
        const y = ((1 - d2.y) / 2) * h;
        const d = Math.hypot(x - cursor.x, y - cursor.y);
        if (d < bestDist) {
          bestDist = d;
          best = f;
          bestX = x;
          bestY = y;
        }
      }

      if (best) {
        readout.style.opacity = '1';
        readout.style.transform = `translate(calc(${bestX}px - 50%), ${
          bestY + 16
        }px)`;
      } else {
        readout.style.opacity = '0';
      }
      if (best?.id !== hovered?.id) {
        setHovered(best);
        onHover(best?.id ?? null);
      }
    };

    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [flights, cameraRef, interactive, hovered, onHover]);

  const ft = hovered ? Math.round((hovered.alt * 3.28084) / 100) * 100 : 0;
  const kt =
    hovered?.velocity != null ? Math.round(hovered.velocity * 1.94384) : null;

  return (
    <div className={styles.readout} ref={readoutRef} style={{ opacity: 0 }}>
      {hovered && (
        <>
          <em>{hovered.callsign ?? hovered.id.toUpperCase()}</em>
          <br />
          {ft.toLocaleString()} ft{kt != null ? ` · ${kt} kt` : ''}
          {hovered.heading != null ? ` · ${Math.round(hovered.heading)}°` : ''}
          {hovered.country ? (
            <>
              <br />
              {hovered.country}
            </>
          ) : null}
        </>
      )}
    </div>
  );
};
